import React, { useEffect } from 'react';

import ErrorReporter from '@lyearn/core/utils/ErrorReporter';
import Platform from '@lyearn/core/utils/Platform';
import { useTranslation } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import { useAccessControl } from '@/components/AccessControl';
import AsyncView from '@/components/AsyncView';
import { AsyncViewProps } from '@/components/AsyncView/types';
import { PageError, withErrorBoundary } from '@/components/Error';
import NavigateBack from '@/components/NavigateBack';
import usePageLayout from '@/components/PageLayout/usePageLayout';
import useWorkspaceAccessControl from '@/components/WorkspaceAccessControl/hooks/useWorkspaceAccessControl';
import useParams from '@/hooks/useParams';
import useQueryParam, { StringParam } from '@/hooks/useQueryParam';
import useRestBlocks from '@/hooks/useRestBlocks';
import DoesNotExist from '@/modules/corporate/components/DoesNotExist';
import ErrorAccess from '@/modules/corporate/components/ErrorAccess';
import ArticleEntity from '@/modules/corporate/modules/article/enitity';
import { useReadArticleQuery } from '@/modules/corporate/modules/article/graphql/mutation/generated/ReadArticle';
import { ArticleParams } from '@/modules/corporate/modules/article/types';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';
import {
  ArticleContentType,
  ResourceActions,
  ResourceStatusEnum,
  ResourceSubjects,
  WorkspaceActions,
  WorkspaceSubjects,
} from '@/types/schema';

import { JOURNEY_SESSION_ID } from '../../../exercise/views/Details/utils';
import useAssignArticle from '../../hooks/useAssignArticle';
import Body from './components/Body';

const ACCESS_DENIED_PROPS: NonNullable<AsyncViewProps<unknown>['AccessDeniedProps']> = {
  showBackButton: true,
  action: WorkspaceActions.Read,
  resource: 'content',
};

function ArticleViewer({
  preview,
  articleId: _articleId,
  hideAccessories,
}: {
  preview?: boolean;
  articleId?: string;
  hideAccessories?: boolean;
}) {
  const { articleId: articleIdParam } = useParams<ArticleParams>();
  const articleId = _articleId || articleIdParam;
  const { t } = useTranslation();
  const { showErrorSnackbar } = useSnackbar();
  const isAdminRoute = useAdminRoute();
  const [journeySessionId] = useQueryParam(JOURNEY_SESSION_ID, StringParam);

  const isNative = Platform.OS !== 'web';

  try {
    usePageLayout({ showHeader: isNative, LeftNode: <NavigateBack />, RightNode: <></> });
  } catch (e) {
    // do nothing;
  }

  const [previewRootBlockId] = useQueryParam('previewRootBlockId', StringParam);

  const [{ data: articleData, error, fetching }, refetch] = useReadArticleQuery({
    variables: { articleId },
    requestPolicy: 'cache-and-network',
  });

  const articleContent = articleData?.content as ArticleContentType | undefined;
  const permission = ArticleEntity.userPermissions(articleContent);
  const canConsume = useAccessControl(
    permission,
    ResourceActions.Consume,
    ResourceSubjects.Resource,
  );
  const canEnroll = useAccessControl(permission, ResourceActions.Enroll, ResourceSubjects.Resource);
  const articleRootBlockId = ArticleEntity.rootBlockId(articleContent);

  const isPreviewing = Boolean(preview);
  const { assignArticle } = useAssignArticle(articleId!);

  const isUnpublishedAvailable =
    isPreviewing && articleContent?.status === ResourceStatusEnum.UnpublishedChanges;
  const { canAccess } = useWorkspaceAccessControl(
    WorkspaceActions.Read,
    WorkspaceSubjects.Resource,
  );

  const {
    data: articleBlocks,
    error: errorBlocks,
    loading: fetchingBlocks,
  } = useRestBlocks(isUnpublishedAvailable ? previewRootBlockId : articleRootBlockId, articleId);

  useEffect(() => {
    if (!isPreviewing && !canConsume && canEnroll && !isAdminRoute) {
      const checkCanRead = assignArticle();
      checkCanRead.catch((err) => {
        ErrorReporter.error(err);
        showErrorSnackbar({ message: t('Common:Error.SomeErrorOccurredTryAgain') });
      });
    }
  }, [assignArticle, canEnroll, canConsume, isPreviewing, showErrorSnackbar, t, isAdminRoute]);

  return (
    <AsyncView
      AccessDeniedProps={ACCESS_DENIED_PROPS}
      data={articleContent}
      fetching={
        fetching ||
        fetchingBlocks ||
        (isUnpublishedAvailable && !previewRootBlockId) ||
        (!isPreviewing && !canConsume && canEnroll)
      }
      isAccessDenied={!canAccess && isPreviewing}
      isEmpty={
        [ResourceStatusEnum.Archived, ResourceStatusEnum.Revoked].includes(
          articleContent?.status!,
        ) && !isPreviewing
      }
      renderData={(articleContent) => {
        if (
          articleContent?.status === ResourceStatusEnum.Revoked ||
          (articleContent?.status === ResourceStatusEnum.Draft && !isPreviewing) ||
          (!isPreviewing && !canConsume)
        ) {
          return <ErrorAccess resource="article" />;
        }

        if (error || !articleRootBlockId || errorBlocks) {
          return <PageError error={error} />;
        }

        return (
          <Body
            key={previewRootBlockId}
            articleBlocks={articleBlocks}
            articleData={articleContent}
            articleId={articleId}
            hideAccessories={hideAccessories}
            isPreviewing={isPreviewing}
            journeySessionId={journeySessionId}
            previewRootBlockId={previewRootBlockId!}
            refetch={refetch}
          />
        );
      }}
      renderEmptyState={() => <DoesNotExist />}
    />
  );
}

export default withErrorBoundary(ArticleViewer, { fallback: PageError });
